import React, { useEffect, useMemo, useState } from "react";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import {
  ArrowRight,
  ArrowLeft,
  Plus,
  X,
  Wand2,
  Sparkles,
  Loader2,
  Rocket,
} from "lucide-react";

const SCAN_PREFILL_KEY = "aw_scan_prefill";

const ENGINES = [
  { key: "openai", label: "ChatGPT", sub: "GPT-5.2", bg: "#E8F4F0", fg: "#10A37F" },
  { key: "anthropic", label: "Claude", sub: "Sonnet 4.5", bg: "#FBEBE2", fg: "#D97757" },
  { key: "gemini", label: "Gemini", sub: "3 Flash", bg: "#E8EFFB", fg: "#4285F4" },
  { key: "perplexity", label: "Perplexity", sub: "(Beta)", bg: "#E5F0F2", fg: "#20808D", beta: true },
  { key: "google_aio", label: "Google AI Overviews", sub: "(Beta)", bg: "#EFEAFA", fg: "#7B5BD9", beta: true },
];

const CATEGORY_META = {
  category: { label: "Category", color: "var(--aw-ink-2)", bg: "var(--aw-surface-2)" },
  comparison: { label: "Comparison", color: "#4285F4", bg: "#E8EFFB" },
  problem_solution: { label: "Problem → Solution", color: "#B97A2B", bg: "#FAEFD8" },
  recommendation: { label: "Recommendation", color: "var(--aw-accent-ink)", bg: "var(--aw-accent-soft)" },
};

function readScanPrefill() {
  try {
    const raw = window.sessionStorage.getItem(SCAN_PREFILL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const STEPS = [
  { id: 1, label: "Brand" },
  { id: 2, label: "Competitors" },
  { id: 3, label: "Engines" },
  { id: 4, label: "Prompts" },
];

export default function Onboarding() {
  const { createProject } = useProject();
  const [scanPrefill] = useState(() => readScanPrefill());
  const [step, setStep] = useState(1);

  // Step 1
  const [name, setName] = useState(scanPrefill?.brand || "");
  const [domain, setDomain] = useState(scanPrefill?.domain || "");
  const [description, setDescription] = useState("");

  // Step 2
  const [competitors, setCompetitors] = useState([]);
  const [newComp, setNewComp] = useState("");
  const [loadingComps, setLoadingComps] = useState(false);
  const [compsError, setCompsError] = useState(null);

  // Step 3
  const [engines, setEngines] = useState(ENGINES.map((e) => e.key));

  // Step 4
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompts, setSelectedPrompts] = useState(new Set());
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [promptCatFilter, setPromptCatFilter] = useState("all");
  const [promptsError, setPromptsError] = useState(null);

  // Final submit
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load competitor suggestions when entering step 2
  useEffect(() => {
    if (step !== 2 || competitors.length > 0 || !name.trim()) return;
    setLoadingComps(true);
    setCompsError(null);
    api
      .post("/onboarding/suggest-competitors", {
        brand: name.trim(),
        domain: domain.trim() || null,
        description: description.trim() || null,
      })
      .then((r) => setCompetitors(r.data.competitors || []))
      .catch(() => setCompsError("Could not load suggestions — add competitors manually below."))
      .finally(() => setLoadingComps(false));
  }, [step, name, domain, description, competitors.length]);

  // Load prompts when entering step 4
  useEffect(() => {
    if (step !== 4 || prompts.length > 0) return;
    setLoadingPrompts(true);
    setPromptsError(null);
    api
      .post("/onboarding/suggest-prompts", {
        brand: name.trim(),
        description: description.trim() || null,
        competitors: competitors,
        count: 50,
      })
      .then((r) => {
        const list = r.data.prompts || [];
        // Inject hero-scan prompt as the first entry if present
        if (scanPrefill?.prompt) {
          list.unshift({ text: scanPrefill.prompt, category: "custom", _fromScan: true });
        }
        setPrompts(list);
        setSelectedPrompts(new Set(list.map((_, i) => i)));
      })
      .catch(() => setPromptsError("Could not generate prompts — try again or add them manually after."))
      .finally(() => setLoadingPrompts(false));
  }, [step, name, description, competitors, prompts.length, scanPrefill]);

  // Persist engines on the Project itself (no more localStorage hack).

  const addComp = () => {
    const c = newComp.trim();
    if (!c) return;
    if (competitors.some((x) => x.toLowerCase() === c.toLowerCase())) {
      setNewComp("");
      return;
    }
    setCompetitors((prev) => [...prev, c]);
    setNewComp("");
  };

  const removeComp = (c) => setCompetitors((prev) => prev.filter((x) => x !== c));

  const toggleEngine = (key) => {
    setEngines((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const togglePrompt = (idx) => {
    setSelectedPrompts((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const filteredPrompts = useMemo(() => {
    if (promptCatFilter === "all") return prompts.map((p, idx) => ({ ...p, _idx: idx }));
    return prompts
      .map((p, idx) => ({ ...p, _idx: idx }))
      .filter((p) => p.category === promptCatFilter);
  }, [prompts, promptCatFilter]);

  const promptCatCounts = useMemo(() => {
    const counts = {};
    prompts.forEach((p) => {
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return counts;
  }, [prompts]);

  const canNext = useMemo(() => {
    if (step === 1) return name.trim().length >= 2;
    if (step === 3) return engines.length >= 1;
    return true;
  }, [step, name, engines]);

  const goNext = () => {
    if (!canNext) return;
    if (step < 4) setStep(step + 1);
    else finalSubmit();
  };
  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const finalSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const proj = await createProject({
        name: name.trim(),
        domain: domain.trim() || null,
        description: description.trim() || null,
        competitors,
        engines,
      });
      // Bulk-add selected prompts
      const selected = Array.from(selectedPrompts)
        .map((i) => prompts[i])
        .filter(Boolean)
        .map((p) => ({
          text: p.text,
          category: p.category === "custom" ? "custom" : p.category,
        }));
      if (selected.length && proj?.project_id) {
        try {
          await api.post(`/projects/${proj.project_id}/prompts/bulk`, {
            prompts: selected,
          });
        } catch {
          // Non-fatal; user can re-add
        }
      }
      // Clear scan prefill
      window.sessionStorage.removeItem(SCAN_PREFILL_KEY);
    } catch (e) {
      setError("Could not create project. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--aw-bg)" }}
      data-testid="onboarding"
    >
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Progress strip */}
        <div className="mb-10" data-testid="onb-progress">
          <div
            className="flex items-center gap-3 mb-4 mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--aw-muted)",
            }}
          >
            <span>Step {step} of {STEPS.length}</span>
            <span>·</span>
            <span style={{ color: "var(--aw-ink)" }}>{STEPS[step - 1].label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {STEPS.map((s) => (
              <div
                key={s.id}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 999,
                  background:
                    s.id < step
                      ? "var(--aw-accent)"
                      : s.id === step
                      ? "var(--aw-ink)"
                      : "var(--aw-border)",
                  transition: "background 0.18s",
                }}
                data-testid={`onb-progress-bar-${s.id}`}
              />
            ))}
          </div>
        </div>

        {scanPrefill?.prompt && step === 1 && (
          <div
            className="mb-6 px-4 py-3 rounded-[10px] flex items-center gap-3 text-sm"
            style={{ background: "var(--aw-accent-soft)", color: "var(--aw-accent-ink)" }}
            data-testid="onb-scan-prefill-banner"
          >
            <Wand2 size={14} className="flex-shrink-0" />
            <div className="flex-1">
              We pre-filled your scan from <strong>{scanPrefill.domain}</strong>.
              Your prompt <em>"{scanPrefill.prompt}"</em> will be added in step 4.
            </div>
          </div>
        )}

        {/* STEP 1 — BRAND */}
        {step === 1 && (
          <section className="card-aw card-aw-lg" data-testid="onb-step-1">
            <div className="eyebrow">Brand</div>
            <h1
              className="mt-2"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 36,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              Who are we tracking?
            </h1>
            <p className="mt-3 text-sm" style={{ color: "var(--aw-muted)", maxWidth: 520 }}>
              The basics — your brand name, your website, and one sentence about
              what you do. Everything else gets auto-suggested from this.
            </p>
            <div className="mt-6 flex flex-col gap-4">
              <div className="scan-field">
                <label htmlFor="onb-name">Brand name *</label>
                <input
                  id="onb-name"
                  className="scan-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Acme"
                  data-testid="onb-name"
                  autoFocus
                />
              </div>
              <div className="scan-field">
                <label htmlFor="onb-domain">Website domain</label>
                <input
                  id="onb-domain"
                  className="scan-input"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="acme.com"
                  data-testid="onb-domain"
                />
              </div>
              <div className="scan-field">
                <label htmlFor="onb-desc">What does your brand do?</label>
                <input
                  id="onb-desc"
                  className="scan-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. B2B payroll for remote teams"
                  data-testid="onb-desc"
                />
                <div className="mt-2 mono" style={{ fontSize: 11, color: "var(--aw-muted)" }}>
                  Used to suggest competitors + generate buyer-stage prompts.
                </div>
              </div>
            </div>
          </section>
        )}

        {/* STEP 2 — COMPETITORS */}
        {step === 2 && (
          <section className="card-aw card-aw-lg" data-testid="onb-step-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow">Competitors</div>
                <h1
                  className="mt-2"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 36,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  Who else might AI suggest?
                </h1>
              </div>
              {loadingComps && (
                <div
                  className="inline-flex items-center gap-2 text-xs mono"
                  style={{ color: "var(--aw-muted)", marginTop: 12 }}
                >
                  <Loader2 className="animate-spin" size={14} /> Asking GPT-5.2…
                </div>
              )}
            </div>
            <p className="mt-3 text-sm" style={{ color: "var(--aw-muted)", maxWidth: 520 }}>
              We asked an LLM for {name}'s most-recognised competitors. Edit, remove,
              or add your own — we'll track every one of them daily.
            </p>
            {compsError && (
              <div
                className="mt-4 text-xs"
                style={{ color: "#C84A2F" }}
                data-testid="onb-comps-error"
              >
                {compsError}
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2" data-testid="onb-comp-chips">
              {competitors.map((c) => (
                <span
                  key={c}
                  className="pill"
                  style={{ height: 32, fontSize: 13, paddingRight: 6 }}
                  data-testid={`onb-comp-${c}`}
                >
                  {c}
                  <button
                    type="button"
                    onClick={() => removeComp(c)}
                    aria-label={`Remove ${c}`}
                    data-testid={`onb-comp-remove-${c}`}
                    style={{
                      marginLeft: 6,
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      border: 0,
                      background: "transparent",
                      color: "var(--aw-muted)",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
              {!competitors.length && !loadingComps && (
                <span className="text-sm" style={{ color: "var(--aw-muted)" }}>
                  No competitors yet — add one below.
                </span>
              )}
            </div>

            <div className="mt-5 flex items-center gap-2">
              <input
                type="text"
                className="scan-input"
                value={newComp}
                onChange={(e) => setNewComp(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addComp();
                  }
                }}
                placeholder="Add another competitor (press Enter)"
                style={{ flex: 1, height: 40 }}
                data-testid="onb-comp-input"
              />
              <button
                type="button"
                onClick={addComp}
                disabled={!newComp.trim()}
                className="btn-aw btn-aw-sm"
                data-testid="onb-comp-add"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </section>
        )}

        {/* STEP 3 — ENGINES */}
        {step === 3 && (
          <section className="card-aw card-aw-lg" data-testid="onb-step-3">
            <div className="eyebrow">Engines</div>
            <h1
              className="mt-2"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 36,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              Which AI engines should we watch?
            </h1>
            <p className="mt-3 text-sm" style={{ color: "var(--aw-muted)", maxWidth: 520 }}>
              All 5 are recommended — daily re-runs across every engine give the
              cleanest signal. You can change this anytime in Settings.
            </p>

            <div className="mt-6 flex flex-col gap-2.5" data-testid="onb-engines">
              {ENGINES.map((eng) => {
                const checked = engines.includes(eng.key);
                return (
                  <label
                    key={eng.key}
                    htmlFor={`eng-${eng.key}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "14px 16px",
                      border: `1px solid ${checked ? "var(--aw-accent)" : "var(--aw-border)"}`,
                      borderRadius: 10,
                      cursor: "pointer",
                      background: checked ? "var(--aw-accent-soft)" : "var(--aw-surface)",
                      transition: "all 0.15s",
                    }}
                    data-testid={`onb-engine-${eng.key}`}
                  >
                    <input
                      id={`eng-${eng.key}`}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEngine(eng.key)}
                      style={{ width: 18, height: 18, accentColor: "var(--aw-accent)", cursor: "pointer" }}
                    />
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: eng.bg,
                        color: eng.fg,
                        fontFamily: "var(--font-mono)",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        flexShrink: 0,
                      }}
                    >
                      {eng.label[0]}
                    </span>
                    <div className="flex-1">
                      <div style={{ fontSize: 15, fontWeight: 500, color: "var(--aw-ink)" }}>
                        {eng.label}
                      </div>
                      <div className="mono" style={{ fontSize: 11, color: "var(--aw-muted)" }}>
                        {eng.sub}
                      </div>
                    </div>
                    {eng.beta && (
                      <span
                        className="pill"
                        style={{
                          fontSize: 10,
                          height: 20,
                          background: "var(--aw-surface-2)",
                          borderColor: "transparent",
                          color: "var(--aw-muted)",
                        }}
                      >
                        Beta
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            {engines.length < 1 && (
              <div
                className="mt-4 text-xs"
                style={{ color: "#C84A2F" }}
                data-testid="onb-engines-error"
              >
                Pick at least one engine.
              </div>
            )}
          </section>
        )}

        {/* STEP 4 — PROMPTS */}
        {step === 4 && (
          <section className="card-aw card-aw-lg" data-testid="onb-step-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="eyebrow">Prompts</div>
                <h1
                  className="mt-2"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 36,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  Your starter prompt library.
                </h1>
              </div>
              {loadingPrompts && (
                <div
                  className="inline-flex items-center gap-2 mono"
                  style={{ fontSize: 11, color: "var(--aw-muted)", marginTop: 12 }}
                >
                  <Loader2 className="animate-spin" size={14} /> Generating 50 prompts…
                </div>
              )}
            </div>
            <p className="mt-3 text-sm" style={{ color: "var(--aw-muted)", maxWidth: 520 }}>
              50 buyer-stage questions tailored to {name || "your brand"}.
              Deselect anything that doesn't fit — you can always add more later.
            </p>
            {promptsError && (
              <div
                className="mt-4 text-xs"
                style={{ color: "#C84A2F" }}
                data-testid="onb-prompts-error"
              >
                {promptsError}
              </div>
            )}

            {/* Category filter */}
            {prompts.length > 0 && (
              <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="onb-prompt-filters">
                <button
                  type="button"
                  onClick={() => setPromptCatFilter("all")}
                  className={`pill ${promptCatFilter === "all" ? "pill-accent" : ""}`}
                  style={{ cursor: "pointer", height: 26, fontSize: 12 }}
                  data-testid="onb-filter-all"
                >
                  All · {prompts.length}
                </button>
                {Object.entries(CATEGORY_META).map(([cat, meta]) => {
                  const count = promptCatCounts[cat] || 0;
                  if (count === 0) return null;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setPromptCatFilter(cat)}
                      className={`pill ${promptCatFilter === cat ? "pill-accent" : ""}`}
                      style={{ cursor: "pointer", height: 26, fontSize: 12 }}
                      data-testid={`onb-filter-${cat}`}
                    >
                      {meta.label} · {count}
                    </button>
                  );
                })}
                <div className="flex-1" />
                <span className="mono" style={{ fontSize: 11, color: "var(--aw-muted)" }}>
                  <strong style={{ color: "var(--aw-ink)" }}>{selectedPrompts.size}</strong> /
                  {" "}{prompts.length} selected
                </span>
              </div>
            )}

            <div
              className="mt-4 flex flex-col gap-1.5"
              style={{ maxHeight: 480, overflowY: "auto", paddingRight: 4 }}
              data-testid="onb-prompts-list"
            >
              {filteredPrompts.map((p) => {
                const idx = p._idx;
                const selected = selectedPrompts.has(idx);
                const meta = CATEGORY_META[p.category] || CATEGORY_META.category;
                return (
                  <label
                    key={idx}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${
                        selected ? "color-mix(in oklch, var(--aw-accent) 25%, var(--aw-border))" : "var(--aw-border)"
                      }`,
                      background: selected
                        ? "color-mix(in oklch, var(--aw-accent-soft) 35%, white)"
                        : "white",
                      cursor: "pointer",
                      transition: "all 0.12s",
                    }}
                    data-testid={`onb-prompt-${idx}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => togglePrompt(idx)}
                      style={{ width: 16, height: 16, accentColor: "var(--aw-accent)", marginTop: 3, cursor: "pointer" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="pill"
                          style={{
                            background: meta.bg,
                            color: meta.color,
                            borderColor: "transparent",
                            fontSize: 10,
                            height: 18,
                            padding: "0 8px",
                          }}
                        >
                          {meta.label}
                        </span>
                        {p._fromScan && (
                          <span
                            className="pill"
                            style={{
                              background: "var(--aw-accent-soft)",
                              color: "var(--aw-accent-ink)",
                              borderColor: "transparent",
                              fontSize: 10,
                              height: 18,
                              padding: "0 8px",
                            }}
                          >
                            <Sparkles size={10} /> From your scan
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: "var(--aw-ink-2)",
                          lineHeight: 1.45,
                        }}
                      >
                        {p.text}
                      </div>
                    </div>
                  </label>
                );
              })}
              {!loadingPrompts && filteredPrompts.length === 0 && !promptsError && (
                <div
                  className="text-center py-10 text-sm"
                  style={{ color: "var(--aw-muted)" }}
                >
                  No prompts in this filter.
                </div>
              )}
            </div>
          </section>
        )}

        {error && (
          <div
            className="mt-4 text-sm"
            style={{ color: "#C84A2F" }}
            data-testid="onb-error"
          >
            {error}
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 1}
            className="btn-aw btn-aw-ghost btn-aw-sm"
            data-testid="onb-back"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-3">
            {step < 4 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!canNext}
                className="btn-aw btn-aw-primary"
                data-testid="onb-next"
              >
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={finalSubmit}
                disabled={submitting || !canNext}
                className="btn-aw btn-aw-primary"
                data-testid="onb-finish"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={14} /> Creating…
                  </>
                ) : (
                  <>
                    <Rocket size={14} /> Create project &amp; add {selectedPrompts.size} prompts
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
